// components/schedule/ScheduleDialog/index.tsx

import { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Typography,
    Box, useTheme, useMediaQuery, Tabs, Tab, IconButton, CircularProgress,
    FormControl, RadioGroup, FormControlLabel, Radio, Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { format } from 'date-fns';

// 型定義
import { ScheduleDialogProps, DialogType } from './types';
import { ScheduleMember } from '../../../types/schedule';

// カスタムフック
import { useScheduleForm } from './useScheduleForm';
import { useScheduleMasterData } from './useScheduleMasterData';
import { useScheduleConflict } from './useScheduleConflict';

// コンポーネント
import { TabPanel } from './TabPanel';
import { ScheduleBasicInfo } from './ScheduleBasicInfo';
import { MemberSelection } from './MemberSelection';
import { VendorSelection } from './VendorSelection';
import { VehicleSelection } from './VehicleSelection';
import { MemberSelectionDialog } from './MemberSelectionDialog';
import { VendorSelectionDialog } from './VendorSelectionDialog';
import { VehicleSelectionDialog } from './VehicleSelectionDialog';

// メンバー・車両の型
import { Member } from '../../../types/member';
import { VendorMember } from '../../../types/vendor';
import { Car } from '../../../types/car';

const ScheduleDialog: React.FC<ScheduleDialogProps> = ({
    open,
    onClose,
    onSave,
    onDelete,
    initialData,
    selectedDate,
    initialTeam,
    isLoading,
    initialScheduleType
}) => {
    // テーマとレスポンシブデザイン用の設定
    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg')); // 大型モニタ (1200px以上)
    const isMediumScreen = useMediaQuery(theme.breakpoints.between('md', 'lg')); // 通常PC
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md')); // タブレット・モバイル

    // タブ関連のstate
    const [currentTab, setCurrentTab] = useState(0);

    // フォーム状態管理
    const {
        scheduleType,
        constructionFormData,
        otherFormData,
        handleScheduleTypeChange,
        handleConstructionChange,
        handleOtherChange,
        handleConstructionRadioChange,
        handleOtherRadioChange,
        handleConstructionTimeTypeChange,
        handleOtherTimeTypeChange,
        handleConstructionSelectChange,
        handleCustomerSelectChange,
        handleCustomerSelect,
        validateConstructionForm,
        validateOtherForm,
        handleConstructionMembersChange,
        handleOtherMembersChange,
        handleConstructionVehiclesChange,
        handleOtherVehiclesChange
    } = useScheduleForm(
        initialData,
        selectedDate,
        initialTeam,
        initialData ? initialData.type : initialScheduleType
    );

    // マスターデータ取得
    const {
        loading,
        loadingMembers,
        members,
        vendors,
        vendorMembers,
        cars,
        constructions,
        customers,
        expandedVendors,
        filteredMembers,
        filteredVendorMembers,
        fetchConstructionMembers,
        handleSearchChange,
        handleVendorExpand,
        setExpandedVendors
    } = useScheduleMasterData(open, scheduleType);

    useEffect(() => {
        // ダイアログが開いたときに明示的にスケジュールタイプをリセット
        if (open) {
            // useScheduleFormから返されたhandleScheduleTypeChangeを使用
            handleScheduleTypeChange(initialData?.type || initialScheduleType || 'construction');
        }
    }, [open, initialData, initialScheduleType]);

    // 登録済み外注メンバーの業者アコーディオンを自動的に開くための useEffect
    useEffect(() => {
        if (open) {
            // 初期状態ではすべてのアコーディオンを閉じる
            setExpandedVendors([]);

            // 既存予定の場合で工事予定タイプなら該当する業者のアコーディオンを開く
            if (initialData?.id && initialData.type === 'construction' && vendorMembers.length > 0) {
                // 現在選択されている外注メンバーの業者IDを取得
                const currentMembers = initialData.memberData || [];

                const selectedVendorIds = (currentMembers as any[])
                    .filter(m => m.VendorMember_ID)
                    .map(m => {
                        const vendorMember = vendorMembers.find(vm => vm.ID === m.VendorMember_ID);
                        return vendorMember?.VendorID;
                    })
                    .filter((id: any): id is number => id !== undefined && id !== null);

                // 重複を除去
                const uniqueVendorIds = Array.from(new Set(selectedVendorIds)) as number[];

                if (uniqueVendorIds.length > 0) {
                    // 業者のアコーディオンを開く
                    setExpandedVendors(uniqueVendorIds);
                }
            }
        }
    }, [open, initialData, vendorMembers]);

    // 予定重複チェック - useScheduleConflictから車両の重複確認機能も取得
    const {
        membersWithOtherSchedules,
        vehiclesWithOtherSchedules,
        checkMemberConflict,
        checkVehicleConflict
    } = useScheduleConflict(scheduleType, constructionFormData, otherFormData);

    // ダイアログの表示状態管理
    const [dialogType, setDialogType] = useState<DialogType>(null);

    // タブの切り替え
    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    // 建設工事ID変更時に担当者を取得
    const handleConstructionIdChange = (e: any) => {
        handleConstructionSelectChange(e);
    };

    // メンバー追加ハンドラ (自社メンバー)
    const handleMemberSelect = async (member: Member) => {
        // 重複チェック
        const canAdd = await checkMemberConflict(member);
        if (!canAdd) return;

        if (scheduleType === 'construction') {
            // 工事予定の場合
            const currentMembers = [...constructionFormData.memberData || []];
            const isAdded = currentMembers.some(m => m.Member_ID === member.ID);

            if (isAdded) {
                // 削除
                const updatedMembers = currentMembers.filter(m => m.Member_ID !== member.ID);
                updateConstructionMembers(updatedMembers);
            } else {
                // 追加
                const newMember = {
                    Member_ID: member.ID,
                    LastName: member.LastName,
                    displayName: member.LastName
                };
                updateConstructionMembers([...currentMembers, newMember]);
            }
        } else {
            // その他予定の場合
            const currentMembers = [...otherFormData.memberData || []];
            const isAdded = currentMembers.some(m => m.Member_ID === member.ID);

            if (isAdded) {
                // 削除
                const updatedMembers = currentMembers.filter(m => m.Member_ID !== member.ID);
                updateOtherMembers(updatedMembers);
            } else {
                // 追加
                const newMember = {
                    Member_ID: member.ID,
                    LastName: member.LastName,
                    displayName: member.LastName
                };
                updateOtherMembers([...currentMembers, newMember]);
            }
        }
    };

    // メンバー削除ハンドラ (自社メンバー)
    const handleMemberRemove = (memberId: number) => {
        if (scheduleType === 'construction') {
            const updatedMembers = constructionFormData.memberData?.filter(
                m => m.Member_ID !== memberId
            ) || [];
            updateConstructionMembers(updatedMembers);
        } else {
            const updatedMembers = otherFormData.memberData?.filter(
                m => m.Member_ID !== memberId
            ) || [];
            updateOtherMembers(updatedMembers);
        }
    };

    // 外注メンバー選択ハンドラ
    const handleVendorMemberSelect = (member: VendorMember, vendorName: string) => {
        if (scheduleType === 'construction') {
            // 工事予定の場合のみ外注メンバーを追加可能
            const currentMembers = [...constructionFormData.memberData || []];
            const isAdded = currentMembers.some(m => m.VendorMember_ID === member.ID);

            if (isAdded) {
                // 削除
                const updatedMembers = currentMembers.filter(m => m.VendorMember_ID !== member.ID);
                updateConstructionMembers(updatedMembers);
            } else {
                // 追加
                const newMember = {
                    VendorMember_ID: member.ID,
                    VendorMemberName: member.MemberName
                };
                updateConstructionMembers([...currentMembers, newMember]);
            }
        }
    };

    // 車両選択ハンドラ - 重複チェック機能を追加
    const handleCarSelect = async (car: Car) => {
        // 重複チェック
        const canAdd = await checkVehicleConflict(car);
        if (!canAdd) return;

        if (scheduleType === 'construction') {
            // 工事予定の場合
            const currentCars = [...constructionFormData.vehicleData || []];
            const isAdded = currentCars.some(v => v.Car_ID === car.ID);

            if (isAdded) {
                // 削除
                const updatedVehicles = currentCars.filter(v => v.Car_ID !== car.ID);
                updateConstructionVehicles(updatedVehicles);
            } else {
                // 追加
                const newVehicle = {
                    Car_ID: car.ID,
                    Car_Model: car.Car_Model,
                    Car_Nickname: car.Car_Nickname,
                    Car_Number: car.Car_Number_4
                };
                updateConstructionVehicles([...currentCars, newVehicle]);
            }
        } else {
            // その他予定の場合
            const currentCars = [...otherFormData.vehicleData || []];
            const isAdded = currentCars.some(v => v.Car_ID === car.ID);

            if (isAdded) {
                // 削除
                const updatedVehicles = currentCars.filter(v => v.Car_ID !== car.ID);
                updateOtherVehicles(updatedVehicles);
            } else {
                // 追加
                const newVehicle = {
                    Car_ID: car.ID,
                    Car_Model: car.Car_Model,
                    Car_Nickname: car.Car_Nickname,
                    Car_Number: car.Car_Number_4
                };
                updateOtherVehicles([...currentCars, newVehicle]);
            }
        }
    };

    // 車両削除ハンドラ
    const handleCarRemove = (carId: number) => {
        if (scheduleType === 'construction') {
            const updatedVehicles = constructionFormData.vehicleData?.filter(
                v => v.Car_ID !== carId
            ) || [];
            updateConstructionVehicles(updatedVehicles);
        } else {
            const updatedVehicles = otherFormData.vehicleData?.filter(
                v => v.Car_ID !== carId
            ) || [];
            updateOtherVehicles(updatedVehicles);
        }
    };

    // メンバーデータ更新（ローカル関数名を変更）
    const updateConstructionMembers = (members: any[]) => {
        // カスタムフックのハンドラを呼び出す
        handleConstructionMembersChange(members);
    };

    // その他の同様の関数も修正
    const updateOtherMembers = (members: any[]) => {
        handleOtherMembersChange(members);
    };

    const updateConstructionVehicles = (vehicles: any[]) => {
        handleConstructionVehiclesChange(vehicles);
    };

    const updateOtherVehicles = (vehicles: any[]) => {
        handleOtherVehiclesChange(vehicles);
    };


    // 送信処理
    const handleSubmit = () => {
        if (scheduleType === 'construction') {
            // 工事予定のバリデーション
            if (!validateConstructionForm()) return;

            // バリデーションが通った場合は保存処理
            onSave(constructionFormData, 'construction');
        } else {
            // その他予定のバリデーション
            if (!validateOtherForm()) return;

            // バリデーションが通った場合は保存処理
            onSave(otherFormData, 'other');
        }
    };

    // 削除処理
    const handleDelete = () => {
        if (!initialData || !initialData.id) return;

        if (onDelete) {
            onDelete(initialData.id, initialData.type);
        }
    };

    // ダイアログオープン処理
    const handleOpenCompanyDialog = () => setDialogType('company');
    const handleOpenVendorDialog = () => {
        // 工事予定のみ外注メンバーを表示
        if (scheduleType === 'construction') {
            const currentMembers = constructionFormData.memberData || [];

            // 現在選択されている外注メンバーの業者IDを取得
            const selectedVendorIds = currentMembers
                .filter((m: any) => m.VendorMember_ID)
                .map((m: any) => {
                    const vendorMember = vendorMembers.find(vm => vm.ID === m.VendorMember_ID);
                    return vendorMember?.VendorID;
                })
                .filter((id): id is number => id !== undefined && id !== null);

            // 重複を除去
            const uniqueVendorIds = Array.from(new Set(selectedVendorIds)) as number[];

            setExpandedVendors(uniqueVendorIds);
            setDialogType('vendor');
        }
    };
    const handleOpenCarDialog = () => setDialogType('car');
    const handleCloseDialog = () => setDialogType(null);

    // チーム名の表示用マッピング
    const teamNameMap = {
        'equipment': '設備班',
        'scaffolding': '仮設班'
    };

    // 現在のチームタイプを取得
    const currentTeamType = scheduleType === 'construction'
        ? constructionFormData.teamType
        : otherFormData.teamType;

    // フォーマットされた日付を表示
    const formattedDate = selectedDate ?
        format(selectedDate, 'yyyy年MM月dd日') :
        (scheduleType === 'construction' ? constructionFormData.scheduleDate : otherFormData.scheduleDate) ?
            format(new Date(scheduleType === 'construction' ? constructionFormData.scheduleDate : otherFormData.scheduleDate), 'yyyy年MM月dd日') : '';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth={false}
            PaperProps={{
                sx: {
                    width: {
                        xs: '95%',
                        sm: '90%',
                        md: '85%',
                        lg: '80%',
                        xl: '75%',
                    },
                    maxWidth: '3200px',
                    height: '90vh',        // 固定高さに設定
                    maxHeight: '90vh',
                    display: 'flex',       // flexレイアウトを適用
                    flexDirection: 'column' // 縦方向に並べる
                }
            }}
            fullScreen={isSmallScreen}
        >
            <DialogTitle sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                pb: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0  // タイトルは縮小しない
            }}>
                <Box>
                    {formattedDate}の
                    {initialData?.id ?
                        (initialData.type === 'construction' ? '工事予定編集' : 'その他予定編集') :
                        (scheduleType === 'construction' ? '工事予定追加' : 'その他予定追加')}
                    {currentTeamType && ` - ${teamNameMap[currentTeamType as keyof typeof teamNameMap]}`}
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{
                flex: 1,               // 残りのスペースをすべて使用
                overflow: 'auto',    // スクロールを無効化
                display: 'flex',       // flexレイアウトを適用
                flexDirection: 'column' // 縦方向に並べる
            }}>
                {/* 予定タイプの選択（新規作成時のみ） */}
                {!initialData?.id && (
                    <Box sx={{ mb: 3, mt: 2, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                        <FormControl component="fieldset">
                            <RadioGroup
                                row
                                name="scheduleType"
                                value={scheduleType}
                                onChange={(e) => handleScheduleTypeChange(e.target.value as 'construction' | 'other')}
                            >
                                <FormControlLabel
                                    value="construction"
                                    control={<Radio />}
                                    label="工事予定"
                                    sx={{
                                        mr: 4,
                                        '& .MuiFormControlLabel-label': { fontWeight: scheduleType === 'construction' ? 'bold' : 'normal' }
                                    }}
                                />
                                <FormControlLabel
                                    value="other"
                                    control={<Radio />}
                                    label="その他予定"
                                    sx={{
                                        '& .MuiFormControlLabel-label': { fontWeight: scheduleType === 'other' ? 'bold' : 'normal' }
                                    }}
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                )}

                {(loading || isLoading) ? (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flex: 1
                    }}>
                        <CircularProgress />
                    </Box>
                ) : isLargeScreen ? (
                    // 大型ディスプレイ用の横並びレイアウト - ここを修正
                    <Grid container spacing={2} sx={{
                        flex: 1,           // 残りのスペースを使う
                        overflow: 'hidden' // スクロールを無効化
                    }}>
                        {/* 基本情報セクション */}
                        <Grid item xs={3} sx={{ height: '100%' }}>
                            <Paper sx={{
                                p: 2,
                                height: '100%',
                                overflow: 'hidden', // 'auto'から'hidden'に変更
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <Typography variant="h6" sx={{ mb: 2, flexShrink: 0 }}>基本情報</Typography>
                                <Box sx={{ flex: 1, overflow: 'auto' }}> {/* スクロール可能な内部コンテナ */}
                                    <ScheduleBasicInfo
                                        scheduleType={scheduleType}
                                        formData={scheduleType === 'construction' ? constructionFormData : otherFormData}
                                        onChange={scheduleType === 'construction' ? handleConstructionChange : handleOtherChange}
                                        onRadioChange={scheduleType === 'construction' ? handleConstructionRadioChange : handleOtherRadioChange}
                                        onTimeTypeChange={scheduleType === 'construction' ? handleConstructionTimeTypeChange : handleOtherTimeTypeChange}
                                        onConstructionSelect={handleConstructionIdChange}
                                        onCustomerSelect={handleCustomerSelectChange}
                                        constructions={constructions}
                                        customers={customers}
                                        isLoadingData={loadingMembers}
                                    />
                                </Box>
                            </Paper>
                        </Grid>

                        {/* 自社メンバー選択セクション */}
                        <Grid item xs={3} sx={{ height: '100%' }}>
                            <Paper sx={{
                                p: 2,
                                height: '100%',
                                overflow: 'hidden', // 'auto'から'hidden'に変更
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <Typography variant="h6" sx={{ mb: 2, flexShrink: 0 }}>自社メンバー</Typography>
                                <Box sx={{ flex: 1, overflow: 'auto' }}> {/* スクロール可能な内部コンテナ */}
                                    <MemberSelection
                                        scheduleType={scheduleType}
                                        members={members}
                                        currentMembers={scheduleType === 'construction' ? constructionFormData.memberData || [] : otherFormData.memberData || []}
                                        onMemberSelect={handleMemberSelect}
                                        onMemberRemove={handleMemberRemove}
                                        membersWithOtherSchedules={membersWithOtherSchedules}
                                    />
                                </Box>
                            </Paper>
                        </Grid>

                        {/* 外注メンバー/顧客選択セクション */}
                        <Grid item xs={3} sx={{ height: '100%' }}>
                            <Paper sx={{
                                p: 2,
                                height: '100%',
                                overflow: 'hidden', // 'auto'から'hidden'に変更
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <Typography variant="h6" sx={{ mb: 2, flexShrink: 0 }}>
                                    {scheduleType === 'construction' ? '外注メンバー' : '顧客選択'}
                                </Typography>
                                <Box sx={{ flex: 1, overflow: 'hidden' }}> {/* スクロールなしの内部コンテナ */}
                                    <VendorSelection
                                        scheduleType={scheduleType}
                                        vendors={vendors}
                                        vendorMembers={vendorMembers}
                                        currentMembers={constructionFormData.memberData || []}
                                        customers={customers}
                                        customerId={otherFormData.customerId}
                                        onVendorMemberSelect={handleVendorMemberSelect}
                                        onCustomerSelect={handleCustomerSelect}
                                        expandedVendors={expandedVendors}
                                        onVendorExpand={handleVendorExpand}
                                    />
                                </Box>
                            </Paper>
                        </Grid>

                        {/* 車両選択セクション */}
                        <Grid item xs={3} sx={{ height: '100%' }}>
                            <Paper sx={{
                                p: 2,
                                height: '100%',
                                overflow: 'hidden', // 'auto'から'hidden'に変更
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <Typography variant="h6" sx={{ mb: 2, flexShrink: 0 }}>車両選択</Typography>
                                <Box sx={{ flex: 1, overflow: 'auto' }}> {/* スクロール可能な内部コンテナ */}
                                    <VehicleSelection
                                        scheduleType={scheduleType}
                                        cars={cars}
                                        currentVehicles={scheduleType === 'construction' ? constructionFormData.vehicleData || [] : otherFormData.vehicleData || []}
                                        onCarSelect={handleCarSelect}
                                        onCarRemove={handleCarRemove}
                                        vehiclesWithOtherSchedules={vehiclesWithOtherSchedules}
                                    />
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                ) : (
                    // PC・モバイル用のタブ切り替えレイアウト

                    <Box
                        sx={{
                            mt: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            height: isSmallScreen ? 'calc(100% - 48px)' : 'auto', // モバイル時は高さを明示的に設定
                            overflow: 'hidden'
                        }}
                    >
                        <Tabs
                            value={currentTab}
                            onChange={handleTabChange}
                            variant={isMediumScreen ? "fullWidth" : "scrollable"}
                            scrollButtons={isSmallScreen ? "auto" : false}
                            sx={{
                                borderBottom: 1,
                                borderColor: 'divider',
                                mb: 2,
                                minHeight: '48px',
                                '& .MuiTabs-flexContainer': {
                                    height: '48px'
                                }
                            }}
                        >
                            <Tab label="基本情報" />
                            <Tab label="自社メンバー" />
                            <Tab label={scheduleType === 'construction' ? "外注メンバー" : "顧客選択"} />
                            <Tab label="車両" />
                        </Tabs>

                        {/* タブコンテンツ全体のコンテナ */}
                        <Box
                            sx={{
                                position: 'relative',
                                flex: 1,
                                height: isSmallScreen ? 'calc(100% - 48px)' : 'auto', // モバイル時は高さを明示的に設定
                                overflow: 'hidden'
                            }}
                        >
                            {/* 基本情報タブ */}
                            <div
                                role="tabpanel"
                                hidden={currentTab !== 0}
                                id="tabpanel-0"
                                aria-labelledby="tab-0"
                                style={{
                                    height: '100%',
                                    overflow: 'auto',
                                    display: currentTab === 0 ? 'block' : 'none'
                                }}
                            >
                                <ScheduleBasicInfo
                                    scheduleType={scheduleType}
                                    formData={scheduleType === 'construction' ? constructionFormData : otherFormData}
                                    onChange={scheduleType === 'construction' ? handleConstructionChange : handleOtherChange}
                                    onRadioChange={scheduleType === 'construction' ? handleConstructionRadioChange : handleOtherRadioChange}
                                    onTimeTypeChange={scheduleType === 'construction' ? handleConstructionTimeTypeChange : handleOtherTimeTypeChange}
                                    onConstructionSelect={handleConstructionIdChange}
                                    onCustomerSelect={handleCustomerSelectChange}
                                    constructions={constructions}
                                    customers={customers}
                                    isLoadingData={loadingMembers}
                                />
                            </div>

                            {/* 自社メンバータブ */}
                            <div
                                role="tabpanel"
                                hidden={currentTab !== 1}
                                id="tabpanel-1"
                                aria-labelledby="tab-1"
                                style={{
                                    height: '100%',
                                    overflow: 'auto',
                                    display: currentTab === 1 ? 'block' : 'none'
                                }}
                            >
                                <MemberSelection
                                    scheduleType={scheduleType}
                                    members={members}
                                    currentMembers={scheduleType === 'construction' ? constructionFormData.memberData || [] : otherFormData.memberData || []}
                                    onMemberSelect={handleMemberSelect}
                                    onMemberRemove={handleMemberRemove}
                                    membersWithOtherSchedules={membersWithOtherSchedules}
                                />
                            </div>

                            {/* 外注メンバー/顧客選択タブ */}
                            <div
                                role="tabpanel"
                                hidden={currentTab !== 2}
                                id="tabpanel-2"
                                aria-labelledby="tab-2"
                                style={{
                                    height: '100%',
                                    overflow: 'auto',
                                    display: currentTab === 2 ? 'block' : 'none'
                                }}
                            >
                                <VendorSelection
                                    scheduleType={scheduleType}
                                    vendors={vendors}
                                    vendorMembers={vendorMembers}
                                    currentMembers={constructionFormData.memberData || []}
                                    customers={customers}
                                    customerId={otherFormData.customerId}
                                    onVendorMemberSelect={handleVendorMemberSelect}
                                    onCustomerSelect={handleCustomerSelect}
                                    expandedVendors={expandedVendors}
                                    onVendorExpand={handleVendorExpand}
                                />
                            </div>

                            {/* 車両タブ */}
                            <div
                                role="tabpanel"
                                hidden={currentTab !== 3}
                                id="tabpanel-3"
                                aria-labelledby="tab-3"
                                style={{
                                    height: '100%',
                                    overflow: 'auto',
                                    display: currentTab === 3 ? 'block' : 'none'
                                }}
                            >
                                <VehicleSelection
                                    scheduleType={scheduleType}
                                    cars={cars}
                                    currentVehicles={scheduleType === 'construction' ? constructionFormData.vehicleData || [] : otherFormData.vehicleData || []}
                                    onCarSelect={handleCarSelect}
                                    onCarRemove={handleCarRemove}
                                    vehiclesWithOtherSchedules={vehiclesWithOtherSchedules}
                                />
                            </div>
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{
                px: 3,
                py: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                flexShrink: 0  // ボタンエリアは縮小しない
            }}>
                {onDelete && initialData?.id && (
                    <Button
                        color="error"
                        onClick={handleDelete}
                        sx={{ mr: 'auto' }}
                    >
                        削除
                    </Button>
                )}
                <Button onClick={onClose}>キャンセル</Button>
                <Button
                    onClick={handleSubmit}
                    color="primary"
                    variant="contained"
                    disabled={isLoading || loading}
                >
                    {isLoading ? <CircularProgress size={24} /> : '登録'}
                </Button>
            </DialogActions>

            {/* メンバー選択ダイアログ - 自社メンバー */}
            <MemberSelectionDialog
                open={dialogType === 'company'}
                onClose={handleCloseDialog}
                members={members}
                currentMembers={scheduleType === 'construction' ? constructionFormData.memberData || [] : otherFormData.memberData || []}
                onMemberSelect={handleMemberSelect}
                membersWithOtherSchedules={membersWithOtherSchedules}
            />

            {/* メンバー選択ダイアログ - 外注メンバー (工事予定のみ) */}
            <VendorSelectionDialog
                open={dialogType === 'vendor'}
                onClose={handleCloseDialog}
                vendors={vendors}
                vendorMembers={vendorMembers}
                currentMembers={constructionFormData.memberData || []}
                onVendorMemberSelect={handleVendorMemberSelect}
                expandedVendors={expandedVendors}
                onVendorExpand={handleVendorExpand}
            />

            {/* 車両選択ダイアログ */}
            <VehicleSelectionDialog
                open={dialogType === 'car'}
                onClose={handleCloseDialog}
                cars={cars}
                currentVehicles={scheduleType === 'construction' ? constructionFormData.vehicleData || [] : otherFormData.vehicleData || []}
                onCarSelect={handleCarSelect}
                vehiclesWithOtherSchedules={vehiclesWithOtherSchedules}
            />
        </Dialog>
    );
};

export default ScheduleDialog;

